import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { css } from 'emotion';
import { useFilters } from 'app/deliveryTool/context/FiltersProvider';
import { useRoadmaps } from 'app/deliveryTool/context/RoadmapsProvider';
import { useMapsLibrary, APIProvider } from "@vis.gl/react-google-maps";
import moment from "moment";
import LeMoulinApi from 'app/universal/utils/LeMoulinApi';
import RouteService from './services/RouteService';

import AutoDeliveriesHeader from './components/AutoDeliveriesHeader';
import OptimizedRoutesList from './components/OptimizedRoutesList';

import { 
  MOULIN_COORDS,
  LOADING_TIME,
  TIME_FOR_RECOVERY,
  INSTALLATION_TIMES,
  DRIVER_WORK_WINDOWS,
  RETURN_TO_DEPOT_TIME,
  MAX_PEOPLE_PER_VEHICLE
} from './constants';
import {
  normalizeAddress,
  getInstallationTime,
  calculateRouteDistance
} from './utils';

// Fonctions utilitaires pour l'optimisation
const calculateInstallationTimeForMergedItems = (mergedItems, firstItemStartAt) => {
  let totalInstallMinutes = 0;
  
  mergedItems.forEach((mergedItem, mergeIdx) => {
    if (mergeIdx === 0) {
      // Premier item : toujours compter le temps d'installation
      const baseInstallMinutes = mergedItem.isRecovery ? TIME_FOR_RECOVERY : getInstallationTime(mergedItem);
      totalInstallMinutes += baseInstallMinutes;
    } else {
      // Autres items : compter seulement si startAt identique au premier
      const currentItemStartAt = mergedItem.start_at;
      if (currentItemStartAt && firstItemStartAt && currentItemStartAt === firstItemStartAt) {
        const baseInstallMinutes = mergedItem.isRecovery ? TIME_FOR_RECOVERY : getInstallationTime(mergedItem);
        totalInstallMinutes += baseInstallMinutes;
      }
      // Si startAt différent, pas de temps d'installation ajouté
    }
  });
  
  return totalInstallMinutes;
};

const getInstallationTimeForItem = (item) => {
  if (item.isMerged && item.mergedItems && item.mergedItems.length > 1) {
    const firstItemStartAt = item.mergedItems[0]?.start_at;
    return calculateInstallationTimeForMergedItems(item.mergedItems, firstItemStartAt);
  } else {
    return item.isRecovery ? TIME_FOR_RECOVERY : getInstallationTime(item);
  }
};

const createDeliveryItem = (delivery, idx) => ({
  id: delivery.id,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  active: true,
  delivery: {
    ...delivery,
    draggableId: delivery.draggableId
  },
  sort: idx + 1,
  recovery: delivery.isRecovery,
  comments: []
});

const createMergedItem = (mergedItem, mergeIdx, firstItemStartAt, arrival, departureTime, travelDuration, setupTime, distance) => {
  // Déterminer le temps d'installation
  let installationTime = 0;
  
  if (mergeIdx === 0) {
    // Premier item : toujours compter le temps d'installation
    installationTime = mergedItem.isRecovery ? TIME_FOR_RECOVERY : getInstallationTime(mergedItem);
  } else {
    // Autres items : compter le temps d'installation seulement si startAt identique au premier
    const currentItemStartAt = mergedItem.start_at;
    if (currentItemStartAt && firstItemStartAt && currentItemStartAt === firstItemStartAt) {
      installationTime = mergedItem.isRecovery ? TIME_FOR_RECOVERY : getInstallationTime(mergedItem);
    } else {
      // startAt différent ou pas de startAt : pas de temps d'installation
      installationTime = 0;
    }
  }
  
  return {
    ...mergedItem,
    arrivalTime: arrival ? arrival.toISOString() : undefined,
    departureTime: departureTime ? departureTime : undefined,
    installationTime: installationTime,
    travelDuration: travelDuration, // Durée du trajet pur en secondes
    setupTime: setupTime / 60,
    distance: distance,
    // Durée du trajet pur vers cette livraison/récupération (en minutes)
    travelDuration: Math.round((travelDuration || 0) / 60),
    draggableId: `${mergedItem.isRecovery ? 'recovery' : 'delivery'}-${mergedItem.id}`,
    isRecovery: mergedItem.isRecovery
  };
};

const sortDeliveryMenByPriority = (deliveryMen) => {
  return deliveryMen.sort((a, b) => {
    // Prioriser les livreurs principaux (primary_driver: true)
    const aIsPrimary = a.primary_driver === true;
    const bIsPrimary = b.primary_driver === true;
    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return 0; // Garder l'ordre original si même priorité
  });
};

const AutoDeliveries = (props) => {
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <AutoDeliveriesContent {...props} />
    </APIProvider>
  );
};

const AutoDeliveriesContent = (props) => {
  const {
    deliveries,
    recoveries
  } = props;

  // On clone les objets pour ne pas modifier les props d'origine
  const deliveriesWithFlag = deliveries.map(delivery => ({ ...delivery, isRecovery: false }));
  const recoveriesWithFlag = recoveries.map(delivery => ({ ...delivery, isRecovery: true }));

  const filters = useFilters();
  const rmData = useRoadmaps();
  const [optimizedRoutes, setOptimizedRoutes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [waitRemaining, setWaitRemaining] = useState(0);
  const [waitScope, setWaitScope] = useState('minute');
  // States stats/loader supprimés après ménage

  const routesLibrary = useMapsLibrary("routes");
  const [directionsService, setDirectionsService] = useState(null);
  const [routeService, setRouteService] = useState(null);

  // Initialize directions service and route service
  useEffect(() => {
    if (!routesLibrary) {
      console.log('Routes library not loaded');
      return;
    }
    console.log('Initializing directions service and route service');
    const newDirectionsService = new routesLibrary.DirectionsService();
    setDirectionsService(newDirectionsService);
    
    // Initialiser le service hybride
    const newRouteService = new RouteService();
    newRouteService.setGoogleMapsDirectionsService(newDirectionsService);
    // Callback d'attente (rate limit) pour afficher une popup avec timer
    newRouteService.setWaitCallbacks(
      (ms, scope) => {
        setWaitScope(scope || 'minute');
        setWaitRemaining(ms);
        // Démarre un timer local pour décrémenter l'affichage
        const startedAt = Date.now();
        const tick = () => {
          const left = Math.max(0, ms - (Date.now() - startedAt));
          setWaitRemaining(left);
          if (left > 0) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      () => {
        setWaitRemaining(0);
      }
    );
    
    setRouteService(newRouteService);
  }, [routesLibrary]);

  // Fonction pour calculer la distance et le temps de trajet entre deux points
  const calculateRouteInfo = async (coords1, coords2, context = {}) => {
    if (!routeService) {
      // Fallback si le service n'est pas encore initialisé
      return { distance: 0, duration: 30 * 60 }; // 30 minutes en secondes
    }

    try {
      return await routeService.calculateRouteInfo(coords1, coords2, context);
    } catch (error) {
      console.error('Erreur lors du calcul de l\'itinéraire:', error);
      return { distance: 0, duration: 30 * 60 };
    }
  };

  // Distance géodésique rapide (Haversine) en mètres, sans appel réseau
  // Formule mathématique pour calculer la distance entre deux points géographiques
  // Plus précise que la distance euclidienne simple, sans coût d'API
  const haversineDistanceMeters = (p1, p2) => {
    if (!p1 || !p2 || typeof p1.lat !== 'number' || typeof p1.lng !== 'number' || typeof p2.lat !== 'number' || typeof p2.lng !== 'number') {
      return Infinity;
    }
    const R = 6371000; // rayon Terre en mètres
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(p2.lat - p1.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const lat1 = toRad(p1.lat);
    const lat2 = toRad(p2.lat);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Fonction pour attacher les récupérations aux livraisons les plus proches
  // 1. Cherche d'abord une correspondance exacte d'adresse
  // 2. Sinon, trouve la livraison la plus proche à vol d'oiseau (sans limite de distance)
  // 3. Utilise la formule Haversine pour le calcul de distance (sans API)
  // 4. Pour les récupérations restantes qui n'ont pas pu être attachées avant l'optimisation
  const attachRecoveriesToDeliveries = async (routes, recoveries) => {
    if (!recoveries || recoveries.length === 0) {
      console.log('Aucune récupération restante à traiter');
      return;
    }
    
    // Créer une liste plate de toutes les livraisons avec leurs routes
    const allDeliveries = [];
    routes.forEach((route, routeIndex) => {
      route.deliveries.forEach((deliveryItem, deliveryIndex) => {
        if (!deliveryItem.recovery) { // Seulement les livraisons, pas les récupérations
          allDeliveries.push({
            ...deliveryItem,
            routeIndex,
            deliveryIndex
          });
        }
      });
    });

    const attachedRecoveries = new Set();
    const remainingRecoveries = [];

    // Pour chaque récupération, trouver la livraison la plus proche
    for (const recovery of recoveries) {
      if (!recovery.lat || !recovery.lng) continue;

      let bestMatch = null;
      let minDistance = Infinity;
      let exactAddressMatch = false;

      // D'abord chercher une correspondance exacte d'adresse
      const recoveryAddress = normalizeAddress(recovery.address);
      
      for (const delivery of allDeliveries) {
        const deliveryAddress = normalizeAddress(delivery.delivery.address);
        
        // Correspondance exacte d'adresse
        if (recoveryAddress && deliveryAddress && recoveryAddress === deliveryAddress) {
          bestMatch = delivery;
          minDistance = 0;
          exactAddressMatch = true;
          break;
        }
      }

      // Si pas de correspondance exacte, chercher la plus proche géographiquement à vol d'oiseau
      if (!exactAddressMatch) {
        for (const delivery of allDeliveries) {
          if (!delivery.delivery.lat || !delivery.delivery.lng) continue;
          
          const distance = haversineDistanceMeters(
            { lat: recovery.lat, lng: recovery.lng },
            { lat: delivery.delivery.lat, lng: delivery.delivery.lng }
          );
          
          if (distance < minDistance) {
            minDistance = distance;
            bestMatch = delivery;
          }
        }
      }

      // Attacher la récupération à la livraison trouvée
      // S'il n'y a pas de correspondance exacte, bestMatch contient la livraison la plus proche à vol d'oiseau
      if (bestMatch) {
        const targetRoute = routes[bestMatch.routeIndex];
        const insertAt = bestMatch.deliveryIndex + 1;
        
        const newRecoveryItem = {
          id: recovery.id,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          active: true,
          delivery: {
            ...recovery,
            draggableId: `recovery-${recovery.id}`,
            isRecovery: true,
            installationTime: Math.max(1, TIME_FOR_RECOVERY + (INSTALLATION_TIMES?.INSTALLATION_VOITURE?.time || 0))
          },
          sort: insertAt + 1,
          recovery: true,
          comments: []
        };

        // Insérer la récupération après la livraison
        targetRoute.deliveries.splice(insertAt, 0, newRecoveryItem);
        
        // Réindexer les sort de tous les éléments de la route
        targetRoute.deliveries.forEach((item, idx) => {
          item.sort = idx + 1;
        });

        attachedRecoveries.add(recovery.id);
        if (exactAddressMatch) {
          console.log(`Récupération ${recovery.id} attachée à la livraison ${bestMatch.delivery.id} (correspondance exacte d'adresse)`);
        } else {
          console.log(`Récupération ${recovery.id} attachée à la livraison ${bestMatch.delivery.id} (distance: ${minDistance.toFixed(1)}m à vol d'oiseau)`);
        }
      } else {
        // Ce cas ne devrait plus arriver car on trouve toujours la livraison la plus proche
        remainingRecoveries.push(recovery);
        console.warn(`Aucune livraison trouvée pour la récupération ${recovery.id} - ce cas ne devrait plus arriver`);
      }
    }

    // Traiter les récupérations restantes
    if (remainingRecoveries.length > 0) {
      console.log(`${remainingRecoveries.length} récupérations restantes à placer dans les groupes les moins chargés`);
      
      // Analyser les groupes par code postal et nombre d'éléments
      const routeAnalysis = routes.map((route, routeIndex) => {
        const deliveries = route.deliveries.filter(d => !d.recovery);
        const recoveries = route.deliveries.filter(d => d.recovery);
        const totalItems = deliveries.length + recoveries.length;
        
        // Extraire les codes postaux des livraisons
        const postalCodes = new Set();
        deliveries.forEach(d => {
          if (d.delivery.postal_code) {
            postalCodes.add(d.delivery.postal_code);
          }
        });
        
        return {
          routeIndex,
          totalItems,
          postalCodes: Array.from(postalCodes),
          deliveriesCount: deliveries.length,
          recoveriesCount: recoveries.length
        };
      });

      // Trier par nombre total d'éléments (croissant)
      routeAnalysis.sort((a, b) => a.totalItems - b.totalItems);

      for (const recovery of remainingRecoveries) {
        let bestRouteIndex = -1;
        let bestScore = -1;
        
        // Extraire le code postal de la récupération
        const recoveryPostalCode = recovery.postal_code;
        
        for (const analysis of routeAnalysis) {
          let score = 0;
          
          // Priorité 1: même code postal
          if (recoveryPostalCode && analysis.postalCodes.includes(recoveryPostalCode)) {
            score += 1000; // Score élevé pour même code postal
          }
          
          // Priorité 2: moins d'éléments total
          score += (100 - analysis.totalItems); // Plus le groupe est petit, plus le score est élevé
          
          if (score > bestScore) {
            bestScore = score;
            bestRouteIndex = analysis.routeIndex;
          }
        }
        
        if (bestRouteIndex >= 0) {
          const targetRoute = routes[bestRouteIndex];
          const insertAt = targetRoute.deliveries.length; // Ajouter à la fin
          
          const newRecoveryItem = {
            id: recovery.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            active: true,
            delivery: {
              ...recovery,
              draggableId: `recovery-${recovery.id}`,
              isRecovery: true,
              installationTime: Math.max(1, TIME_FOR_RECOVERY + (INSTALLATION_TIMES?.INSTALLATION_VOITURE?.time || 0))
            },
            sort: insertAt + 1,
            recovery: true,
            comments: []
          };

          // Ajouter la récupération à la fin de la route
          targetRoute.deliveries.push(newRecoveryItem);
          
          // Mettre à jour l'analyse pour les prochaines récupérations
          const analysis = routeAnalysis.find(a => a.routeIndex === bestRouteIndex);
          if (analysis) {
            analysis.totalItems++;
            analysis.recoveriesCount++;
            if (recoveryPostalCode && !analysis.postalCodes.includes(recoveryPostalCode)) {
              analysis.postalCodes.push(recoveryPostalCode);
            }
            // Retrier l'analyse
            routeAnalysis.sort((a, b) => a.totalItems - b.totalItems);
          }
          
          console.log(`Récupération restante ${recovery.id} placée dans la route ${targetRoute.name} (code postal: ${recoveryPostalCode || 'non défini'})`);
        }
      }
    }

    // Note: Les distances ne sont plus recalculées ici car elles proviennent directement d'ORS
    // Si des récupérations ont été ajoutées, les distances ORS restent une bonne approximation
    // car les récupérations sont généralement proches des livraisons existantes
    console.log(`Attachement des récupérations terminé. ${attachedRecoveries.size} récupérations attachées, ${remainingRecoveries.length} placées dans les groupes les moins chargés.`);
  };

  // Bouton test ORS Optimization: crée un jeu minimal jobs/vehicles
  const handleOptimizeSchedules = async () => {
    if (!routeService) return;
    setIsLoading(true);
    setError(null);
    try {
      // Calcule distance et durée totales via Google Directions pour un ordre donné (départ/arrivée Moulin)
      const computeRouteTotalsWithGoogle = async (orderedDeliveries) => {
        try {
          if (!directionsService || !window?.google?.maps) return null;
          const maxWaypoints = 23; // Google permet ~25 points avec origine+destination
          const waypoints = orderedDeliveries.map(p => ({
            location: new window.google.maps.LatLng(p.lat, p.lng),
            stopover: true
          }));
          if (waypoints.length > maxWaypoints) return null;
          const origin = new window.google.maps.LatLng(MOULIN_COORDS.lat, MOULIN_COORDS.lng);
          const destination = new window.google.maps.LatLng(MOULIN_COORDS.lat, MOULIN_COORDS.lng);
          const resp = await directionsService.route({
            origin,
            destination,
            waypoints,
            travelMode: window.google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
            provideRouteAlternatives: false
          });
          const legs = resp?.routes?.[0]?.legs || [];
          if (legs.length === 0) return null;
          const distance = legs.reduce((acc, leg) => acc + (leg?.distance?.value || 0), 0);
          const duration = legs.reduce((acc, leg) => acc + (leg?.duration?.value || 0), 0);
          return { distance, duration };
        } catch (_) {
          return null;
        }
      };
      // Normalise une valeur (date/heure ou heure seule) sur la date du filtre
      const normalizeToFilterDate = (value) => {
        if (!value) return null;
        const filterDay = moment(filters.date).startOf('day');
        let m = moment(value);
        if (!m.isValid()) {
          const asTime = moment(value, ['HH:mm', 'HH:mm:ss'], true);
          if (asTime.isValid()) {
            return filterDay.clone().set({
              hour: asTime.hour(),
              minute: asTime.minute(),
              second: asTime.second(),
              millisecond: 0
            });
          }
          return null;
        }
        // Cas où la date est un placeholder (ex: 1970-01-01T09:00:00)
        if (m.year() <= 1971) {
          return filterDay.clone().set({
            hour: m.hour(),
            minute: m.minute(),
            second: m.second(),
            millisecond: 0
          });
        }
        return m.tz('Europe/Paris', true).utc();
      };
      // 1) Séparer en 3 groupes par contrainte horaire: <=10:00, <=13:00, >13:00
      // Pour les récupérations, on ne regarde pas les horaires, elles seront attachées après
      const cutoff10 = moment(filters.date).set({ hour: 10, minute: 0, second: 0, millisecond: 0 });
      const cutoff13 = moment(filters.date).set({ hour: 13, minute: 0, second: 0, millisecond: 0 });

      // Séparer les livraisons des récupérations pour le traitement
      const deliveriesOnly = deliveriesWithFlag.filter(d => d.lat && d.lng);
      const recoveriesOnly = recoveriesWithFlag.filter(d => d.lat && d.lng);

      // Attacher les récupérations aux livraisons les plus proches AVANT l'optimisation
      const deliveriesWithAttachedRecoveries = [...deliveriesOnly];
      const attachedRecoveries = new Set();
      
      // Créer une map des livraisons par position pour l'attachement
      const deliveryPositions = deliveriesOnly.map((delivery, index) => ({
        delivery,
        index,
        lat: delivery.lat,
        lng: delivery.lng,
        address: delivery.address
      }));

      // Attacher les récupérations aux livraisons les plus proches
      for (const recovery of recoveriesOnly) {
        if (!recovery.lat || !recovery.lng) continue;

        let bestMatch = null;
        let minDistance = Infinity;
        let exactAddressMatch = false;

        // D'abord chercher une correspondance exacte d'adresse
        const recoveryAddress = normalizeAddress(recovery.address);
        
        for (const deliveryPos of deliveryPositions) {
          const deliveryAddress = normalizeAddress(deliveryPos.address);
          
          // Correspondance exacte d'adresse
          if (recoveryAddress && deliveryAddress && recoveryAddress === deliveryAddress) {
            bestMatch = deliveryPos;
            minDistance = 0;
            exactAddressMatch = true;
            break;
          }
        }

        // Si pas de correspondance exacte, chercher la plus proche géographiquement à vol d'oiseau
        if (!exactAddressMatch) {
          for (const deliveryPos of deliveryPositions) {
            if (!deliveryPos.lat || !deliveryPos.lng) continue;
            
            const distance = haversineDistanceMeters(
              { lat: recovery.lat, lng: recovery.lng },
              { lat: deliveryPos.lat, lng: deliveryPos.lng }
            );
            
            if (distance < minDistance) {
              minDistance = distance;
              bestMatch = deliveryPos;
            }
          }
        }

        // Attacher la récupération à la livraison trouvée
        // S'il n'y a pas de correspondance exacte, bestMatch contient la livraison la plus proche à vol d'oiseau
        if (bestMatch) {
          // Créer un objet récupération avec les propriétés nécessaires pour l'optimisation
          const attachedRecovery = {
            ...recovery,
            isRecovery: true,
            attachedToDeliveryId: bestMatch.delivery.id,
            distance: minDistance
          };
          
          // Ajouter la récupération après la livraison dans la liste
          const insertIndex = bestMatch.index + 1;
          deliveriesWithAttachedRecoveries.splice(insertIndex, 0, attachedRecovery);
          
          // Mettre à jour les indices des livraisons suivantes
          for (let i = insertIndex + 1; i < deliveryPositions.length; i++) {
            deliveryPositions[i].index++;
          }
          
          attachedRecoveries.add(recovery.id);
          if (exactAddressMatch) {
            console.log(`Récupération ${recovery.id} attachée à la livraison ${bestMatch.delivery.id} (correspondance exacte d'adresse)`);
          } else {
            console.log(`Récupération ${recovery.id} attachée à la livraison ${bestMatch.delivery.id} (distance: ${minDistance.toFixed(1)}m à vol d'oiseau)`);
          }
        } else {
          // Ce cas ne devrait plus arriver car on trouve toujours la livraison la plus proche
          console.warn(`Aucune livraison trouvée pour la récupération ${recovery.id} - ce cas ne devrait plus arriver`);
        }
      }

      // Les récupérations restantes seront traitées séparément après l'optimisation
      const remainingRecoveries = recoveriesOnly.filter(r => !attachedRecoveries.has(r.id));

      const groupEarly = [];
      const groupMid = [];
      const groupLate = [];

      // Traiter les livraisons ET les récupérations attachées pour la répartition horaire
      for (const d of deliveriesWithAttachedRecoveries) {
        const endAt = normalizeToFilterDate(d.arrived_at_end);
        const startAt = normalizeToFilterDate(d.arrived_at_start);
        // Groupe en priorité sur le début de créneau (start), sinon sur la fin (end)
        const refTime = startAt || endAt;
        if (!refTime) {
          groupLate.push(d);
          continue;
        }
        if (refTime.isSameOrBefore(cutoff10)) groupEarly.push(d);
        else if (refTime.isBefore(cutoff13)) groupMid.push(d);
        else groupLate.push(d);
      }

      const batches = [
        { label: '≤10h', items: groupEarly },
        { label: '≤13h', items: groupMid },
        { label: '≥13h', items: groupLate }
      ].filter(b => b.items.length > 0);

      // 2) Traiter tous les batches en parallèle (tests Network visibles immédiatement)
      const resultsByBatch = await Promise.all(batches.map(async (batch, batchIdx) => {
        console.log(`Traitement du batch ${batch.label} (${batch.items.length} livraisons + récupérations attachées)`);
        // ORS: traite les livraisons ET les récupérations attachées
        const itemsForJobs = batch.items.slice(0, 100);
        // Les récupérations sont maintenant incluses dans les jobs pour l'optimisation
        const batchAttachedRecoveryIds = new Set();

        const jobIdToItem = new Map();
        
        // Fonction pour fusionner les jobs avec les mêmes coordonnées
        const mergeJobsAtSameLocation = (items) => {
          const locationMap = new Map();
          const mergedItems = [];
          
          items.forEach((item, idx) => {
            const coordsKey = `${item.lat},${item.lng}`;
            
            if (locationMap.has(coordsKey)) {
              // Fusionner avec l'item existant
              const existingItem = locationMap.get(coordsKey);
              existingItem.mergedItems = existingItem.mergedItems || [existingItem];
              existingItem.mergedItems.push(item);
              
              // Somme des personnes
              existingItem.person = (existingItem.person || 0) + (item.person || 0);
              
              // Garder le créneau le plus restrictif (le plus tôt pour start, le plus tard pour end)
              if (item.arrived_at_start && (!existingItem.arrived_at_start || 
                  normalizeToFilterDate(item.arrived_at_start).isBefore(normalizeToFilterDate(existingItem.arrived_at_start)))) {
                existingItem.arrived_at_start = item.arrived_at_start;
              }
              if (item.arrived_at_end && (!existingItem.arrived_at_end || 
                  normalizeToFilterDate(item.arrived_at_end).isAfter(normalizeToFilterDate(existingItem.arrived_at_end)))) {
                existingItem.arrived_at_end = item.arrived_at_end;
              }
              
              // Marquer comme fusionné
              existingItem.isMerged = true;
              existingItem.originalIds = existingItem.originalIds || [existingItem.id];
              existingItem.originalIds.push(item.id);
              
              console.log(`Fusion de ${item.id} avec ${existingItem.id} à ${coordsKey} (${existingItem.person} personnes total)`);
            } else {
              // Premier item à cette localisation
              const newItem = { ...item };
              newItem.mergedItems = [newItem];
              newItem.originalIds = [newItem.id];
              locationMap.set(coordsKey, newItem);
              mergedItems.push(newItem);
            }
          });
          
        const mergedCount = mergedItems.filter(item => item.isMerged).length;
        console.log(`Fusion terminée: ${items.length} items → ${mergedItems.length} items fusionnés (${mergedCount} fusions effectuées)`);
        return mergedItems;
        };
        
        // Fusionner les items avec les mêmes coordonnées
        const mergedItemsForJobs = mergeJobsAtSameLocation(itemsForJobs);
        
        const jobs = mergedItemsForJobs.map((it, idx) => {
          const jobId = idx + 1;
          jobIdToItem.set(jobId, it);
          if (it.isRecovery) batchAttachedRecoveryIds.add(it.id);
          
          // Pour les récupérations attachées, utiliser les créneaux de la livraison associée
          let twStart, twEnd, priRef;
          if (it.isRecovery && it.attachedToDeliveryId) {
            // Trouver la livraison associée pour récupérer ses créneaux
            const attachedDelivery = itemsForJobs.find(item => item.id === it.attachedToDeliveryId);
            if (attachedDelivery) {
              twStart = normalizeToFilterDate(attachedDelivery.arrived_at_start);
              twEnd = normalizeToFilterDate(attachedDelivery.arrived_at_end);
              priRef = twEnd || twStart;
            }
          } else {
            twStart = normalizeToFilterDate(it.arrived_at_start);
            twEnd = normalizeToFilterDate(it.arrived_at_end);
            
            // Calculer le temps d'installation pour cette livraison
            const baseInstallMinutes = getInstallationTimeForItem(it);
            const serviceMinutes = Math.max(1, baseInstallMinutes);
            
            // Ajuster la time window de fin pour respecter le temps d'installation
            if (it.start_at && twEnd) {
              const startAtMoment = normalizeToFilterDate(it.start_at);
              
              // Calculer l'heure limite d'arrivée = start_at - temps d'installation
              const maxArrivalTime = startAtMoment.clone().subtract(serviceMinutes, 'minutes');
              
              // Si l'heure limite d'arrivée est avant la fin de la time window actuelle,
              // alors ajuster la time window de fin
              if (maxArrivalTime.isBefore(twEnd)) {
                // S'assurer que la time window de fin reste après ou égale à la time window de début
                if (maxArrivalTime.isAfter(twStart) || maxArrivalTime.isSame(twStart)) {
                  twEnd = maxArrivalTime;
                } else {
                  // Si l'ajustement rend la time window invalide, utiliser start_at comme fin sauf si le start_at est avant la time window de début
                  if (startAtMoment.isAfter(twStart) || startAtMoment.isSame(twStart)) {
                    twEnd = startAtMoment;
                  } else {
                    twEnd = twStart;
                  }
                }
              }
            }
            
            priRef = twEnd || twStart;
          }
          
          // Calculer le temps de service
          const baseInstallMinutes = getInstallationTimeForItem(it);
          const serviceMinutes = Math.max(1, baseInstallMinutes);
          
          // Pour les récupérations, définir les time_windows selon le groupe
          let recoveryTimeWindows = undefined;
          if (it.isRecovery) {
            const day = moment(filters.date);
            let recoveryStart, recoveryEnd;
            
            // Calculer l'heure de début minimum pour les récupérations
            // basée sur les créneaux des livraisons du même groupe
            let minRecoveryStart = null;
            
            // Parcourir toutes les livraisons du batch pour trouver le créneau le plus précoce + temps d'installation
            for (const delivery of itemsForJobs) {
              if (!delivery.isRecovery) { // Seulement les livraisons, pas les récupérations
                const deliveryStart = normalizeToFilterDate(delivery.arrived_at_start);
                // Utiliser le début du créneau de la livraison + temps d'installation
                if (deliveryStart) {
                  // Calculer l'heure de fin de la livraison (début + temps d'installation)
                  const baseInstallMinutes = getInstallationTimeForItem(delivery);
                  
                  // Pour le calcul des time_windows, on inclut le temps de voiture car c'est le pire cas
                  const serviceMinutes = Math.max(1, baseInstallMinutes + (INSTALLATION_TIMES?.INSTALLATION_VOITURE?.time || 0));
                  const deliveryEndTime = deliveryStart.clone().add(serviceMinutes, 'minutes');
                  
                  if (!minRecoveryStart || deliveryEndTime.isBefore(minRecoveryStart)) {
                    minRecoveryStart = deliveryEndTime;
                  }
                }
              }
            }
            
            // Utiliser l'heure de début minimum calculée ou l'heure de début de la fenêtre
            let effectiveStart;
            if (minRecoveryStart) {
              effectiveStart = minRecoveryStart.unix();
            } else {
              // Fallback si aucune livraison trouvée
              if (batch.label === '≤10h') {
                effectiveStart = day.clone().set({ 
                  hour: DRIVER_WORK_WINDOWS.MORNING.start, 
                  minute: 0, 
                  second: 0, 
                  millisecond: 0 
                }).unix();
              } else if (batch.label === '≤13h') {
                effectiveStart = day.clone().set({ 
                  hour: DRIVER_WORK_WINDOWS.MID_MORNING.start, 
                  minute: 0, 
                  second: 0, 
                  millisecond: 0 
                }).unix();
              } else if (batch.label === '≥13h') {
                effectiveStart = day.clone().set({ 
                  hour: DRIVER_WORK_WINDOWS.AFTERNOON.start, 
                  minute: 0, 
                  second: 0, 
                  millisecond: 0 
                }).unix();
              }
            }
            
            // Définir la fin de la fenêtre selon le batch
            if (batch.label === '≤10h') {
              recoveryEnd = day.clone().set({ 
                hour: Math.floor(DRIVER_WORK_WINDOWS.MORNING.end), 
                minute: (DRIVER_WORK_WINDOWS.MORNING.end % 1) * 60 - RETURN_TO_DEPOT_TIME, 
                second: 0, 
                millisecond: 0 
              }).unix();
            } else if (batch.label === '≤13h') {
              recoveryEnd = day.clone().set({ 
                hour: Math.floor(DRIVER_WORK_WINDOWS.MID_MORNING.end), 
                minute: (DRIVER_WORK_WINDOWS.MID_MORNING.end % 1) * 60 - RETURN_TO_DEPOT_TIME, 
                second: 0, 
                millisecond: 0 
              }).unix();
            } else if (batch.label === '≥13h') {
              recoveryEnd = day.clone().set({ 
                hour: DRIVER_WORK_WINDOWS.AFTERNOON.end, 
                minute: 0 - RETURN_TO_DEPOT_TIME, 
                second: 0, 
                millisecond: 0 
              }).unix();
            }
            
            recoveryStart = effectiveStart;
            recoveryTimeWindows = [[recoveryStart, recoveryEnd]];
            // Log pour debug des time_windows des récupérations
            if (minRecoveryStart) {
              console.log(`Récupération ${it.id}: time_windows basée sur le créneau le plus précoce + temps d'installation (${minRecoveryStart.format('HH:mm')})`);
            }
          }
          
          return {
            id: jobId,
            // Temps de service pour une livraison ou récupération
            service: Math.max(60, (serviceMinutes * 60) || 300),
            setup: INSTALLATION_TIMES?.INSTALLATION_VOITURE?.time * 60 || 0,
            location: [it.lng, it.lat],
            time_windows: recoveryTimeWindows || ((twStart && twEnd) ? [[ twStart.unix(), twEnd.unix() ]] : undefined),
            priority: 10,
            delivery: [it.person < MAX_PEOPLE_PER_VEHICLE ? it.person : MAX_PEOPLE_PER_VEHICLE] // nombre de personnes de la prestation. On met une limite max de MAX_PEOPLE_PER_VEHICLE personnes
          };
        });

        // Construire autant de véhicules que possible (basé sur les livreurs si dispo)
        // Filtrer les livreurs actifs et les trier par priorité (primary_driver en premier)
        // Les livreurs avec primary_driver: true sont prioritaires pour l'attribution des routes
        const allActiveDeliveryMen = Array.isArray(rmData?.deliveryMen) ? rmData.deliveryMen.filter(dm => dm.active === true) : [];
        const deliveryMen = sortDeliveryMenByPriority(allActiveDeliveryMen);
        
        // Log des livreurs sélectionnés avec leur statut
        const primaryDrivers = deliveryMen.filter(dm => dm.primary_driver === true);
        const regularDrivers = deliveryMen.filter(dm => dm.primary_driver !== true);
        console.log(`Livreurs sélectionnés pour le batch ${batch.label}: ${primaryDrivers.length} principal(s), ${regularDrivers.length} régulier(s)`);
        if (primaryDrivers.length > 0) {
          console.log('Livreurs principaux:', primaryDrivers.map(dm => dm.name || dm.id));
        }
        
        const ORS_MAX_VEHICLES = 3; // Limite ORS actuelle
        const maxVehicles = Math.min(
          ORS_MAX_VEHICLES,
          Math.max(deliveryMen.length, 1),
          Math.max(1, jobs.length)
        );
        if (Math.max(deliveryMen.length, 1) > ORS_MAX_VEHICLES) {
          console.warn(`ORS: trop de véhicules demandés (${Math.max(deliveryMen.length, 1)}), limitation à ${ORS_MAX_VEHICLES}.`);
        }

        // Fenêtre de travail du véhicule selon période (avec pauses des livreurs)
        const day = moment(filters.date);
        let vehStartMoment, vehEndMoment;
        
        if (batch.label === '≤10h') {
          // Matin : 7h00 - 9h30 (retour au dépôt à 9h30 pour pause à 9h45)
          vehStartMoment = day.clone().set({ 
            hour: DRIVER_WORK_WINDOWS.MORNING.start, 
            minute: 0, 
            second: 0, 
            millisecond: 0 
          });
          vehEndMoment = day.clone().set({ 
            hour: Math.floor(DRIVER_WORK_WINDOWS.MID_MORNING.end), 
            minute: (DRIVER_WORK_WINDOWS.MID_MORNING.end % 1) * 60 - RETURN_TO_DEPOT_TIME, 
            second: 0, 
            millisecond: 0 
          });
        } else if (batch.label === '≤13h') {
          // Mi-matin : 10h00 - 12h15 (retour au dépôt à 12h15 pour pause à 12h30)
          vehStartMoment = day.clone().set({ 
            hour: DRIVER_WORK_WINDOWS.MID_MORNING.start, 
            minute: 0, 
            second: 0, 
            millisecond: 0 
          });
          vehEndMoment = day.clone().set({ 
            hour: Math.floor(DRIVER_WORK_WINDOWS.MID_MORNING.end), 
            minute: (DRIVER_WORK_WINDOWS.MID_MORNING.end % 1) * 60 - RETURN_TO_DEPOT_TIME, 
            second: 0, 
            millisecond: 0 
          });
        } else if (batch.label === '≥13h') {
          // Après-midi : 13h00 - 14h45 (retour au dépôt à 14h45 pour fin à 15h00)
          vehStartMoment = day.clone().set({ 
            hour: DRIVER_WORK_WINDOWS.AFTERNOON.start, 
            minute: 0, 
            second: 0, 
            millisecond: 0 
          });
          vehEndMoment = day.clone().set({ 
            hour: DRIVER_WORK_WINDOWS.AFTERNOON.end, 
            minute: 0 - RETURN_TO_DEPOT_TIME, 
            second: 0, 
            millisecond: 0 
          });
        }
        const vehStart = vehStartMoment.unix();
        const vehEnd = vehEndMoment.unix();

        const vehicles = Array.from({ length: maxVehicles }).map((_, i) => ({
          id: i + 1,
          profile: 'driving-car',
          start: [MOULIN_COORDS.lng, MOULIN_COORDS.lat],
          end: [MOULIN_COORDS.lng, MOULIN_COORDS.lat],
          setup: LOADING_TIME * 60,
          capacity: [MAX_PEOPLE_PER_VEHICLE], // MAX_PEOPLE_PER_VEHICLE personnes par tournée
          time_window: [vehStart, vehEnd],
          speed_factor: 0.7 // 70% de la vitesse normale
        }));

        console.log(`Fenêtre de travail pour le batch ${batch.label}: ${vehStartMoment.format('HH:mm')} - ${vehEndMoment.format('HH:mm')} (retour au dépôt ${RETURN_TO_DEPOT_TIME}min avant la pause)`);

        // Associer chaque véhicule ORS à un livreur (si dispo)
        // Les livreurs sont déjà triés par priorité (principaux en premier)
        const selectedDeliveryMen = deliveryMen.slice(0, maxVehicles);
        const vehicleIdToDeliveryMan = new Map();
        vehicles.forEach((v, idx) => {
          const man = selectedDeliveryMen[(idx) % selectedDeliveryMen.length] || null;
          vehicleIdToDeliveryMan.set(v.id, man);
        });
        
        // Log de l'attribution des véhicules
        console.log(`Attribution des véhicules pour le batch ${batch.label}:`);
        selectedDeliveryMen.forEach((driver, idx) => {
          const isPrimary = driver.primary_driver === true;
          console.log(`  Véhicule ${idx + 1}: ${driver.name || driver.id} ${isPrimary ? '(PRINCIPAL)' : '(régulier)'}`);
        });

        const options = { g: true };

        const result = await routeService.optimizeSchedulesWithORS(jobs, vehicles, options);
        console.log(`ORS Optimization result [${batch.label}]:`, result);
        console.log(`Break de chargement ajouté au départ: ${LOADING_TIME} minutes`);

        const newRoutesFromORS = [];
        const routesResp = Array.isArray(result?.routes) ? result.routes : [];

        for (let rIdx = 0; rIdx < routesResp.length; rIdx++) {
          console.log(`Traitement de la route ${rIdx + 1} de ${routesResp.length}`);
          const r = routesResp[rIdx];
          const steps = Array.isArray(r?.steps) ? r.steps : [];
          const jobSteps = steps.filter(s => s.type === 'job' && (typeof s.id === 'number' || typeof s.job === 'number'));

          const deliveriesList = jobSteps
            .map((s, stepIndex) => {
              const jobId = typeof s.id === 'number' ? s.id : s.job;
              const it = jobIdToItem.get(jobId);
              if (!it) return null;
              
              // Respect de l'horaire ORS: arrival/duration
              const arrival = typeof s.arrival === 'number' ? new Date(s.arrival * 1000) : null;
              
              // Calculer le temps d'installation total pour le calcul du temps de départ
              const totalInstallMinutes = getInstallationTimeForItem(it);
              
              // Départ = arrivée + setupTime + serviceTime (temps d'installation total)
              const departureTime = arrival ? new Date(arrival.getTime() + (s.setup + totalInstallMinutes * 60) * 1000) : null;
              
              // Calculer la durée du trajet pur vers cette livraison
              let travelDuration = s.duration;
              
              // Si c'est un job fusionné, créer un item pour chaque livraison/récupération fusionnée
              if (it.isMerged && it.mergedItems && it.mergedItems.length > 1) {
                console.log(`Défusion du job ${jobId} en ${it.mergedItems.length} items à ${it.lat},${it.lng}`);
                
                // Récupérer le startAt de la première livraison pour comparaison
                const firstItemStartAt = it.mergedItems[0]?.start_at;
                
                return it.mergedItems.map((mergedItem, mergeIdx) => 
                  createMergedItem(mergedItem, mergeIdx, firstItemStartAt, arrival, departureTime, travelDuration, s.setup, s.distance)
                );
              } else {
                // Job normal (non fusionné)
                const baseInstallMinutes = it.isRecovery ? TIME_FOR_RECOVERY : getInstallationTime(it);
                return {
                  ...it,
                  arrivalTime: arrival ? arrival.toISOString() : undefined,
                  departureTime: departureTime ? departureTime : undefined,
                  installationTime: baseInstallMinutes,
                  travelDuration: travelDuration, // Durée du trajet pur en secondes
                  setupTime: s.setup / 60,
                  distance: s.distance
                };
              }
            })
            .filter(Boolean)
            .flat() // Aplatir les arrays de jobs fusionnés
            .map(d => ({
              ...d,
              // Le temps d'installation affiché ne comprend que le temps de service, pas le temps de voiture
              installationTime: d.installationTime,
              // Durée du trajet pur vers cette livraison/récupération (en minutes)
              travelDuration: Math.round((d.travelDuration || 0) / 60),
              draggableId: `${d.isRecovery ? 'recovery' : 'delivery'}-${d.id}`,
              isRecovery: d.isRecovery
            }));

          // Les récupérations sont maintenant incluses comme jobs dans l'optimisation ORS
          const deliveriesListWithRecoveries = deliveriesList;

          // Utiliser directement les données d'ORS pour distance et durée (plus performant)
          let distanceMeters = 0;
          let durationSeconds = 0;
          
          // Récupérer la distance et durée depuis la réponse ORS
          if (r.distance !== undefined) {
            distanceMeters = r.distance; // Distance en mètres depuis ORS
          }
          if (r.duration !== undefined) {
            durationSeconds = r.duration; // Durée en secondes depuis ORS
          }
          
          console.log(`Route ${rIdx + 1} - Données ORS: ${distanceMeters}m (${Math.round(distanceMeters / 100) / 10}km), ${durationSeconds}s`);
          
          // Fallback si les données ORS ne sont pas disponibles
          if (distanceMeters === 0 || durationSeconds === 0) {
            console.warn(`Données ORS manquantes pour la route ${rIdx + 1}, utilisation du fallback Google`);
            const googleTotals = await computeRouteTotalsWithGoogle(deliveriesListWithRecoveries);
            if (googleTotals) {
              distanceMeters = googleTotals.distance;
              durationSeconds = googleTotals.duration;
            } else {
              // Fallback: calcul interne (distance) + somme des durées par paires
              try {
                const { distance } = await calculateRouteDistance(deliveriesListWithRecoveries, directionsService, calculateRouteInfo);
                distanceMeters = distance || 0;
              } catch (_) {}
              try {
                const seq = [MOULIN_COORDS, ...deliveriesListWithRecoveries.map(p => ({ lat: p.lat, lng: p.lng })), MOULIN_COORDS];
                for (let i = 0; i < seq.length - 1; i++) {
                  const { duration } = await calculateRouteInfo(seq[i], seq[i + 1], { isBetweenDeliveries: true });
                  durationSeconds += duration || 0;
                }
              } catch (_) {}
            }
          }

          // Heure de départ depuis le first 'start' step arrival
          let startHour = null;
          const startArrival = steps.find(s => s.type === 'start')?.arrival;
          if (typeof startArrival === 'number') {
            const startAtDepot = new Date(startArrival * 1000);
            // Prendre en compte le temps de chargement au Moulin avant départ
            startHour = new Date(startAtDepot.getTime());
          }



          const deliveryItems = deliveriesListWithRecoveries.map(createDeliveryItem);

          const vehicleId = (typeof r?.vehicle === 'number') ? r.vehicle : (rIdx + 1);
          const assignedDeliveryMan = vehicleIdToDeliveryMan.get(vehicleId) || null;

          const newRoute = {
            id: 0, // reindexé après agrégation
            subIndex: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            active: true,
            delivery_man: assignedDeliveryMan,
            name: `Route ${batch.label} #${rIdx + 1}`.trim(),
            date: moment(filters.date).format('YYYY-MM-DD'),
            start_hour: startHour,
            distance: distanceMeters, // Distance en mètres depuis ORS
            distance_km: Math.round(distanceMeters / 100) / 10, // Distance en km avec 1 décimale
            duration: durationSeconds, // Durée en secondes depuis ORS
            deliveries: deliveryItems
          };

          newRoutesFromORS.push(newRoute);
        }

        if (newRoutesFromORS.length === 0) {
          console.warn(`ORS Optimization: aucune route pour le batch ${batch.label}`);
        }

        // Les récupérations seront traitées globalement après l'optimisation de tous les batches

        return newRoutesFromORS;
      }));

      const allNewRoutes = resultsByBatch.flat();
      // Réindexer les IDs pour éviter toute collision
      let reindexed = allNewRoutes.map((r, idx) => ({
        ...r,
        id: (optimizedRoutes?.length || 0) + idx + 1
      }));

      // Éviter les chevauchements de livreurs: réaffectation si overlap (start_hour/duration + services)
      // Filtrer les livreurs actifs et les trier par priorité (primary_driver en premier)
      const allActiveDeliveryMen = Array.isArray(rmData?.deliveryMen) ? rmData.deliveryMen.filter(dm => dm.active === true) : [];
      const deliveryMen = sortDeliveryMenByPriority(allActiveDeliveryMen);
      console.log('deliveryMen (triés par priorité):', deliveryMen.map(dm => `${dm.name || dm.id}${dm.primary_driver ? ' (PRINCIPAL)' : ''}`));
      const driverIdToAvailability = new Map(); // driverId -> lastEndTime (Date)

      const estimateRouteEnd = (route) => {
        const start = route.start_hour ? new Date(route.start_hour) : null;
        if (!start || Number.isNaN(start.getTime())) return null;
        const drivingSec = Math.max(0, Number(route.duration || 0));
        const serviceSec = (Array.isArray(route.deliveries) ? route.deliveries : []).reduce((acc, item) => {
          const instMin = Number(item?.delivery?.installationTime || 0);
          return acc + Math.max(0, instMin * 60);
        }, 0);
        return new Date(start.getTime() + (drivingSec + serviceSec) * 1000);
      };

      // Trier par heure de début pour affectation séquentielle
      reindexed = [...reindexed].sort((a, b) => {
        const ta = a.start_hour ? new Date(a.start_hour).getTime() : 0;
        const tb = b.start_hour ? new Date(b.start_hour).getTime() : 0;
        return ta - tb;
      }).map(route => ({ ...route }));

      for (let i = 0; i < reindexed.length; i++) {
        const route = reindexed[i];
        const start = route.start_hour ? new Date(route.start_hour) : null;
        const end = estimateRouteEnd(route);
        if (!start || !end) continue;

        const assigned = route.delivery_man || null;
        const hasConflict = assigned && driverIdToAvailability.has(assigned.id) && driverIdToAvailability.get(assigned.id) > start;

        let finalDriver = assigned;
        if (hasConflict) {
          // Chercher un autre livreur disponible
          const candidate = deliveryMen.find(dm => {
            if (!dm || typeof dm.id === 'undefined') return false;
            const lastEnd = driverIdToAvailability.get(dm.id) || null;
            return !lastEnd || lastEnd <= start;
          });
          if (candidate) {
            finalDriver = candidate;
          } else {
            // Aucun disponible, on garde l'existant
            finalDriver = assigned;
          }
        }

        if (finalDriver && typeof finalDriver.id !== 'undefined') {
          route.delivery_man = finalDriver;
          driverIdToAvailability.set(finalDriver.id, end);
        }
      }

      // Attribution des livreurs en fonction des codes postaux/villes préférés
      console.log('Attribution des livreurs selon les codes postaux/villes préférés...');
      
      // Fonction pour normaliser une chaîne (supprimer espaces, accents, majuscules)
      const normalizeString = (str) => {
        if (!str) return '';
        return str
          .toLowerCase()
          .trim()
          .replace(/\s+/g, '') // Supprimer tous les espaces
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, ''); // Supprimer les accents
      };
      
      for (let i = 0; i < reindexed.length; i++) {
        const route = reindexed[i];
        const currentDriver = route.delivery_man;
        
        // Extraire les codes postaux et villes des livraisons de cette route
        const routeLocations = new Set();
        route.deliveries.forEach(deliveryItem => {
          if (deliveryItem.delivery) {
            // Ajouter le code postal s'il existe
            if (deliveryItem.delivery.postal_code) {
              routeLocations.add(normalizeString(deliveryItem.delivery.postal_code));
            }
            // Ajouter la ville s'il existe
            if (deliveryItem.delivery.city) {
              routeLocations.add(normalizeString(deliveryItem.delivery.city));
            }
          }
        });
        
        if (routeLocations.size === 0) {
          console.log(`Route ${route.id}: aucun code postal/ville trouvé, garde le livreur actuel`);
          continue;
        }
        
        console.log(`Route ${route.id}: localisations ${Array.from(routeLocations).join(', ')}`);
        
        // Chercher un livreur avec des codes postaux/villes préférés qui correspondent
        let bestDriver = currentDriver;
        let bestMatchCount = 0;
        let bestIsPrimary = currentDriver?.primary_driver === true;
        
        for (const driver of deliveryMen) {
          if (!driver || !driver.preferred_sectors || !Array.isArray(driver.preferred_sectors)) {
            continue;
          }
          
          // Normaliser les préférences du livreur
          const normalizedPreferences = driver.preferred_sectors.map(pref => normalizeString(pref));
          
          // Compter les correspondances entre localisations de la route et préférences du livreur
          const matchCount = normalizedPreferences.filter(preferredLocation => 
            routeLocations.has(preferredLocation)
          ).length;
          
          const isPrimary = driver.primary_driver === true;
          
          // Prioriser les livreurs principaux même avec le même nombre de correspondances
          const shouldSelect = matchCount > bestMatchCount || 
            (matchCount === bestMatchCount && isPrimary && !bestIsPrimary);
          
          if (shouldSelect) {
            // Vérifier que ce livreur est disponible pour cette route
            const driverStart = route.start_hour ? new Date(route.start_hour) : null;
            const driverEnd = estimateRouteEnd(route);
            
            if (driverStart && driverEnd) {
              const lastEnd = driverIdToAvailability.get(driver.id) || null;
              const isAvailable = !lastEnd || lastEnd <= driverStart;
              
              if (isAvailable) {
                bestMatchCount = matchCount;
                bestDriver = driver;
                bestIsPrimary = isPrimary;
                console.log(`Route ${route.id}: livreur ${driver.name || driver.id} a ${matchCount} correspondances de localisations${isPrimary ? ' (PRINCIPAL)' : ''}`);
              }
            }
          }
        }
        
        // Si on a trouvé un meilleur livreur, l'assigner
        if (bestDriver && bestDriver.id !== currentDriver?.id) {
          const primaryStatus = bestDriver.primary_driver === true ? ' (PRINCIPAL)' : '';
          console.log(`Route ${route.id}: attribution du livreur ${bestDriver.name || bestDriver.id}${primaryStatus} (${bestMatchCount} localisations correspondantes)`);
          
          // Mettre à jour la disponibilité
          const routeEnd = estimateRouteEnd(route);
          if (routeEnd) {
            driverIdToAvailability.set(bestDriver.id, routeEnd);
          }
          
          route.delivery_man = bestDriver;
        } else if (bestMatchCount > 0) {
          const currentPrimaryStatus = currentDriver?.primary_driver === true ? ' (PRINCIPAL)' : '';
          console.log(`Route ${route.id}: garde le livreur actuel ${currentDriver?.name || currentDriver?.id}${currentPrimaryStatus} (${bestMatchCount} correspondances)`);
        } else {
          console.log(`Route ${route.id}: aucun livreur avec localisations préférées trouvé`);
        }
      }

      // Restaurer l'ordre d'origine (par id reindexé)
      reindexed.sort((a, b) => a.id - b.id);

      // Ajouter en plus des routes existantes (sans les remplacer)
      setOptimizedRoutes(prev => [...prev, ...reindexed]);

      // Traiter les récupérations restantes qui n'ont pas pu être attachées avant l'optimisation
      if (remainingRecoveries.length > 0) {
        console.log(`Traitement de ${remainingRecoveries.length} récupérations restantes après optimisation (cas d'erreur)`);
        await attachRecoveriesToDeliveries(reindexed, remainingRecoveries);
      } else {
        console.log('Toutes les récupérations ont été attachées avant l\'optimisation ORS');
      }

    } catch (e) {
      console.error(e);
      setError(e.message || 'Erreur ORS Optimization');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAllRoadmaps = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Sauvegarder chaque itinéraire séquentiellement
      for (const route of optimizedRoutes) {
        if (!route || !route.deliveries || route.deliveries.length === 0) {
          console.error('Aucune livraison à sauvegarder pour cet itinéraire');
          continue;
        }

        const deliveriesData = route.deliveries.map((delivery) => ({
          delivery: delivery.delivery.id,
          sort: delivery.sort,
          recovery: delivery?.recovery ? 1 : 0,
        }));

        const postRoadmap = await LeMoulinApi.request.roadmap.post({
          date: filters.date,
          deliveryMan: route.delivery_man?.id || null,
          distance: route.distance,
          startHour: route.start_hour ? moment(route.start_hour).format('HH:mm') : null,
          manualStartHour: route.start_hour ? moment(route.start_hour).format('HH:mm') : null,
          deliveries: deliveriesData,
        });

        if (postRoadmap) {
          // Mettre à jour la liste des itinéraires
          const updatedRoadmaps = [...(rmData?.roadmapsInit || [])];
          updatedRoadmaps.push(postRoadmap);
          rmData.setRoadmapsInit(updatedRoadmaps);
          
          // Appeler la fonction pour supprimer l'itinéraire optimisé
          if (props.onItemsUpdated) {
            const savedDeliveryIds = new Set(
              route.deliveries.map(delivery => delivery.delivery.id)
            );
            props.onItemsUpdated(savedDeliveryIds);
          }
        } else {
          console.error('Erreur lors de la création de la roadmap');
        }
      }

      // Redirection vers la liste des itinéraires avec rechargement
      window.location.href = `/itineraires${window.location.search}`;

    } catch (err) {
      setError('Une erreur est survenue lors de la sauvegarde des itinéraires');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRoadmapSaved = async (roadmapId) => {
    // Trouver l'itinéraire à sauvegarder
    const routeToSave = optimizedRoutes.find(route => route.id === roadmapId);
    if (!routeToSave) {
      console.error('Itinéraire non trouvé');
      return;
    }

    try {
      const deliveriesData = routeToSave.deliveries.map((delivery) => ({
        delivery: delivery.delivery.id,
        sort: delivery.sort,
        recovery: delivery?.recovery ? 1 : 0,
      }));

      const postRoadmap = await LeMoulinApi.request.roadmap.post({
        date: filters.date,
        deliveryMan: routeToSave.delivery_man?.id || null,
        distance: routeToSave.distance,
        startHour: routeToSave.start_hour ? moment(routeToSave.start_hour).format('HH:mm') : null,
        manualStartHour: routeToSave.start_hour ? moment(routeToSave.start_hour).format('HH:mm') : null,
        deliveries: deliveriesData,
      });

      if (postRoadmap) {
        // Mettre à jour la liste des itinéraires
        const updatedRoadmaps = [...(rmData?.roadmapsInit || [])];
        updatedRoadmaps.push(postRoadmap);
        rmData.setRoadmapsInit(updatedRoadmaps);
        
        // Filtrer l'itinéraire sauvegardé de la liste des itinéraires optimisés
        setOptimizedRoutes(prevRoutes => prevRoutes.filter(route => route.id !== roadmapId));
        
        // Filtrer les livraisons et récupérations qui ont été sauvegardées
        const savedDeliveryIds = new Set(
          routeToSave.deliveries.map(delivery => delivery.delivery.id)
        );
        
        // Mettre à jour la liste des items (livraisons non attribuées)
        if (props.onItemsUpdated) {
          props.onItemsUpdated(savedDeliveryIds);
        }
      } else {
        console.error('Erreur lors de la création de la roadmap');
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde de l\'itinéraire:', err);
    }
  };

  return (
    <div className={css({
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    })}>
      <AutoDeliveriesHeader 
        isLoading={isLoading}
        optimizedRoutes={optimizedRoutes}
        error={error}
        onSaveAll={handleSaveAllRoadmaps}
        onOptimizeSchedules={handleOptimizeSchedules}
        waitRemaining={waitRemaining}
        waitScope={waitScope}
      />
      <OptimizedRoutesList 
        routes={optimizedRoutes}
        onRoadmapSaved={handleRoadmapSaved}
      />
    </div>
  );
};

AutoDeliveries.propTypes = {
  deliveries: PropTypes.array.isRequired,
  recoveries: PropTypes.array.isRequired,
  onItemsUpdated: PropTypes.func.isRequired,
};

export default AutoDeliveries; 